import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Sparkles, Loader2, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function HosysAILogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Benvingut a HOSYS AI");
      navigate("/HosysAI");
    } catch (err) {
      toast.error(err.message || "Credencials incorrectes");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white flex items-center justify-center px-4 relative overflow-hidden" data-testid="hosysai-login">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 text-sm" data-testid="login-back">
          <ArrowLeft className="w-4 h-4" /> Tornar
        </Link>

        <div className="p-8 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-lg leading-none">HOSYS<span className="text-cyan-300"> AI</span></p>
              <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Academy & Compliance</p>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-white">Accedeix al teu compte</h1>
          <p className="text-sm text-slate-400 mt-1">Gestiona la governança d'IA de la teva organització.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-400"
                placeholder="nom@empresa.com" required autoFocus
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs text-slate-400 uppercase tracking-wider">Contrasenya</label>
                <Link to="/forgot-password" className="text-xs text-cyan-300 hover:text-cyan-200">He oblidat la contrasenya</Link>
              </div>
              <div className="mt-1 relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 pr-10 text-sm text-white focus:outline-none focus:border-cyan-400"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button" onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-white"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Accedir
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <p className="text-sm text-slate-400">
              Encara no tens compte?{" "}
              <Link to="/register" className="text-cyan-300 hover:text-cyan-200 font-medium">Crear empresa</Link>
            </p>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Powered by <a href="https://hosys.es" className="text-cyan-400 hover:text-cyan-300">Ho-Sys</a>
        </p>
      </div>
    </div>
  );
}
